// The warm-up line for the session's first main lift (walkthrough 3): the
// empty bar for 10, then ~50% × 5, ~70% × 3 and ~85% × 1 of the working
// load, each rounded to the bar's step — or, for a lift still finding its
// weight (D9), a generic ramp for its kit (W13). Warm-up sets aren't
// logged (D16).
import { formatLoad } from "./format";
import { loadStep, rackOf, roundToStep } from "./steps";

// The empty bar: a standard 45 lb / 20 kg barbell.
export const EMPTY_BAR = { lb: 45, kg: 20 };
const RAMP = [
  [0.5, 5],
  [0.7, 3],
  [0.85, 1],
];

/**
 * The warm-up as steps: `[{load, reps, empty?}]` — the empty bar first,
 * then the ramp, leaving out a step that wouldn't be heavier than the one
 * before it (at or below the empty bar, or a repeat after rounding) or
 * that reaches the working load. null without a working load.
 */
export function warmupSteps(load, barStep, units = "lb") {
  const working = Number(load);
  if (!(working > 0)) return null;
  const bar = EMPTY_BAR[units] ?? EMPTY_BAR.lb;
  const steps = [{ load: bar, reps: 10, empty: true }];
  for (const [share, reps] of RAMP) {
    const value = roundToStep(working * share, barStep);
    const previous = steps[steps.length - 1].load;
    if (value > previous && value < working) steps.push({ load: value, reps });
  }
  return steps;
}

/**
 * "Empty bar × 10 · 70 lb × 5 · 95 lb × 3 · 115 lb × 1", or null when
 * there's no working load to warm up to.
 */
export function warmupLine(load, barStep, units = "lb") {
  const steps = warmupSteps(load, barStep, units);
  if (!steps) return null;
  return steps
    .map((step) =>
      step.empty ? `Empty bar × ${step.reps}` : `${formatLoad(step.load, units)} × ${step.reps}`,
    )
    .join(" · ");
}

// Ours: with no working load yet there's nothing to take percentages of, so
// the line says how to ramp, by what the lift is loaded with (steps.rackOf).
const FIND_WEIGHT_WARMUPS = {
  barbell: "Empty bar × 10, then 3–4 jumps up to your first working set",
  dumbbells: "A light pair × 10, then 2–3 jumps up to your first working set",
  cable: "A light weight × 10, then 2–3 jumps up to your first working set",
  bodyweight: "A few easy reps — with a band's help if you like — then rest before your first working set",
  band: "A lighter band × 10, then your first working set",
  other: "Something light × 10, then 2–3 jumps up to your first working set",
};

/** The warm-up for a lift still finding its weight, by its rack. */
export function findWeightWarmup(rack) {
  return FIND_WEIGHT_WARMUPS[rack] ?? FIND_WEIGHT_WARMUPS.other;
}

/**
 * The warm-up for a plan's first main lift: `{key, name, line}`, or null.
 * A barbell lift with a working load ramps to it (warmupLine); one still
 * finding its weight gets its kit's generic line (W13). A loaded lift off
 * the barbell (a weighted chin-up) has no ramp to give, so the next main
 * lift is tried. `byKey` maps exercise keys to the catalog's exercises.
 */
export function warmupFor(items, byKey, settings, units) {
  for (const item of items) {
    const exercise = byKey.get(item.exercise);
    if ((item.role ?? exercise?.role) !== "main") continue;
    const rack = rackOf(exercise);
    const name = item.name ?? exercise?.name ?? item.exercise;
    if (item.find_weight) return { key: item.exercise, name, line: findWeightWarmup(rack) };
    if (rack !== "barbell") continue;
    // (null without a working load: the next main lift is tried)
    const line = warmupLine(item.load, loadStep(exercise, settings, units), units);
    if (line) return { key: item.exercise, name, line };
  }
  return null;
}
